/**
 * What a medications sync must write so the EMR supplement and dispensed lists mirror the
 * patient's orders. Pure: it returns the PATCH bodies for /:patientId/update-emr-supplement
 * and /:patientId/update-emr-dispensed, and a sync that finds the lists already in step
 * returns none.
 *
 * Mirroring is intended (EMRv2 syncOrdersWithEMR and the old EMR's syncOrderItemsWithEMR
 * both replicate it): an item on an active order is shown active, and an item the sync
 * added from an order is moved to history once no active order holds it. What it must
 * not do is undo a clinician's decision:
 *
 *  - An ended (historical) or unticked item comes back only when an order actually
 *    re-added it: an order dated or created after the item was ended, or, for an item the
 *    sync itself ended because it left every order, any active order holding it again.
 *    The order's updatedAt is not used: any save of the order (a note, a lab, a restore)
 *    bumps it, and that reactivated every historical supplement still on an old order.
 *  - Every write names the EMR entry by its _id (itemId). Lists hold several entries with
 *    the same name; the backend matched the first, so moving the second to history
 *    re-stamped the first one's endDate on every sync instead.
 */

export const ENDED_BY_SYNC = 'sync';

const toTime = (value) => {
  if (!value) return null;
  const time = new Date(value).getTime();
  return Number.isFinite(time) ? time : null;
};

const latest = (...values) => values.map(toTime).reduce(
  (max, time) => (time === null ? max : max === null ? time : Math.max(max, time)),
  null,
);

const DAY_MS = 24 * 60 * 60 * 1000;
// Phoenix is UTC-7 all year (no daylight saving)
const PHOENIX_UTC_OFFSET_MS = 7 * 60 * 60 * 1000;

// Treatment dates are stored as a calendar day at UTC midnight (new Date('YYYY-MM-DD')),
// which is 5 pm the day before in Phoenix. Such a date counts from the start of its day
// in Phoenix; any other time is used as it is.
const orderDayTime = (value) => {
  const time = toTime(value);
  if (time === null) return null;
  return time % DAY_MS === 0 ? time + PHOENIX_UTC_OFFSET_MS : time;
};

// When an order put the item on: the later of the order's date and its creation. An
// order dated ahead (a scheduled treatment) counts from its creation until its day comes,
// or it would bring back, on every sync until then, an item a clinician ended today.
const orderAddedAt = (orderItem, nowTime) => latest(
  ...[orderItem.treatmentDate, orderItem.date].map(orderDayTime).filter((time) => (time ?? Infinity) <= nowTime),
  orderItem.treatmentCreatedAt,
);

// When the EMR item was last ended or unticked
const emrEndedAt = (emrItem) => latest(emrItem.endDate, emrItem.lastModified, emrItem.startDate);

const isShownActive = (emrItem) => emrItem.checked === true && emrItem.status !== 'historical';

const shouldReactivate = (emrItem, orderItem, nowTime) => {
  if (isShownActive(emrItem)) return false;
  if (emrItem.status === 'historical' && emrItem.endedBy === ENDED_BY_SYNC) return true;
  const addedAt = orderAddedAt(orderItem, nowTime);
  const endedAt = emrEndedAt(emrItem);
  // An item with no dates at all is left as it is, as before
  return addedAt !== null && endedAt !== null && addedAt > endedAt;
};

// The entry a name refers to when there are several: the one changed last
const pickEntry = (entries) => entries.reduce((best, entry) => (
  !best || (toTime(entry.lastModified) ?? 0) > (toTime(best.lastModified) ?? 0) ? entry : best
), null);

const withItemId = (body, emrItem) => (emrItem?._id ? { itemId: String(emrItem._id), ...body } : body);

// Order supplements: the newest order's entry per name, active only
const normalizeOrderSupplements = (orderSupplements = []) =>
  orderSupplements.filter((item) => item && item.status === 'active');

// Active dispensed: { _doc, treatmentDate, treatmentCreatedAt, updatedAt } per name, or flat
const normalizeOrderDispensed = (orderDispensed = []) => orderDispensed
  .map((entry) => {
    const item = entry?._doc || entry;
    if (!item) return null;
    return {
      ...item,
      treatmentDate: entry.treatmentDate ?? item.treatmentDate,
      treatmentCreatedAt: entry.treatmentCreatedAt ?? item.treatmentCreatedAt,
    };
  })
  .filter((item) => item && Number(item.quantity) > 0);

const planList = (emrItems, orderItems, { now, newItemStartDate }) => {
  const patches = [];
  const orderNames = new Set(orderItems.map((item) => item.name));

  // Order-sourced items no longer on any active order go to history, once
  for (const orphan of emrItems) {
    if (orphan.status === 'active' && orphan.checked === true && orphan.source === 'order' && !orderNames.has(orphan.name)) {
      patches.push(withItemId({
        name: orphan.name, checked: false, status: 'historical',
        frequency: orphan.frequency || '', selectedField: orphan.selectedField || '',
        source: 'order', startDate: orphan.startDate, endDate: now, endedBy: ENDED_BY_SYNC,
      }, orphan));
    }
  }

  // Items on active orders: added when missing, brought back when an order re-added them
  const seen = new Set();
  for (const orderItem of orderItems) {
    if (seen.has(orderItem.name)) continue;
    seen.add(orderItem.name);
    const entries = emrItems.filter((emrItem) => emrItem.name === orderItem.name);
    if (entries.length === 0) {
      patches.push({
        name: orderItem.name, checked: true, status: 'active',
        frequency: orderItem.frequency || '', selectedField: orderItem.selectedField || '',
        source: 'order', startDate: newItemStartDate(orderItem),
      });
      continue;
    }
    if (entries.some(isShownActive)) continue;
    const entry = pickEntry(entries);
    if (!shouldReactivate(entry, orderItem, toTime(now))) continue;
    patches.push(withItemId({
      name: orderItem.name, checked: true, status: 'active',
      frequency: orderItem.frequency || entry.frequency || '',
      selectedField: orderItem.selectedField || entry.selectedField || '',
      source: 'order', startDate: orderItem.startDate || entry.startDate, endDate: null,
    }, entry));
  }
  return patches;
};

/**
 * @param {object} lists
 * @param {object[]} lists.emrSupplements  GET /:patientId/get-emr-supplements → supplements
 * @param {object[]} lists.emrDispensed    GET /:patientId/get-emr-dispensed → dispensed
 * @param {object[]} lists.orderSupplements GET /patient/:customerId/supplements → orderSupplements
 * @param {object[]} lists.orderDispensed  GET /active-dispensed-items/:customerId
 * @param {Date} [now]
 * @returns {{ supplements: object[], dispensed: object[] }} PATCH bodies, in order
 */
export const planMedicationSync = ({
  emrSupplements = [], emrDispensed = [], orderSupplements = [], orderDispensed = [],
}, now = new Date()) => {
  const nowIso = now.toISOString();
  return {
    supplements: planList(emrSupplements, normalizeOrderSupplements(orderSupplements), {
      now: nowIso,
      newItemStartDate: (orderItem) => orderItem.startDate || nowIso,
    }),
    dispensed: planList(emrDispensed, normalizeOrderDispensed(orderDispensed), {
      now: nowIso,
      newItemStartDate: () => nowIso,
    }),
  };
};

// What of an order the sync reads: its supplements and dispensed items with a quantity.
// Two snapshots that match mean nothing the sync mirrors changed.
export const orderMedicationSignature = (order) => {
  if (!order || !Array.isArray(order.userSupplements) || !Array.isArray(order.userdispensed)) return null;
  const pick = (items, fields) => items
    .filter((item) => item && Number(item.quantity) > 0)
    .map((item) => fields.map((field) => JSON.stringify(item[field] ?? null)).join('|'))
    .sort()
    .join('\n');
  return [
    pick(order.userSupplements, ['name', 'quantity', 'status', 'frequency', 'selectedField']),
    pick(order.userdispensed, ['name', 'quantity', 'frequency', 'selectedField']),
  ].join('\n--\n');
};

// Whether closing a popup on this order needs a sync: only when what the sync mirrors
// changed while it was open. Unknown (no snapshot) syncs, as before.
export const orderMedicationsChanged = (before, after) => {
  if (before === null || before === undefined) return true;
  return before !== orderMedicationSignature(after);
};
